import React from 'react';
import { Coffee } from '@phosphor-icons/react';

interface LoadingSpinnerProps {
  message?: string;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

export const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({ 
  message = "Laden...", 
  size = 'md',
  className = '' 
}) => {
  const sizeClasses = {
    sm: 'w-6 h-6',
    md: 'w-8 h-8',
    lg: 'w-12 h-12'
  };

  const containerSizeClasses = {
    sm: 'gap-2',
    md: 'gap-3',
    lg: 'gap-4'
  };

  const textSizeClasses = {
    sm: 'text-sm',
    md: 'text-base',
    lg: 'text-lg'
  };

  return (
    <div className={`flex flex-col items-center justify-center ${containerSizeClasses[size]} ${className}`}>
      {/* Coffee cup with steam animation */}
      <div className="relative">
        {/* Steam lines */}
        <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 flex space-x-1">
          <div 
            className="w-0.5 h-3 bg-neutral-secondary-mid rounded-full opacity-60 animate-pulse" 
            style={{ animationDelay: '0ms', animationDuration: '1200ms' } as React.CSSProperties}
          ></div>
          <div 
            className="w-0.5 h-4 bg-neutral-secondary-mid rounded-full opacity-70 animate-pulse" 
            style={{ animationDelay: '200ms', animationDuration: '1200ms' } as React.CSSProperties}
          ></div>
          <div 
            className="w-0.5 h-3 bg-neutral-secondary-mid rounded-full opacity-60 animate-pulse" 
            style={{ animationDelay: '400ms', animationDuration: '1200ms' } as React.CSSProperties}
          ></div>
        </div>
        
        {/* Coffee cup icon with rotation */}
        <div className="animate-spin" style={{ animationDuration: '3s' } as React.CSSProperties}>
          <Coffee 
            className={`${sizeClasses[size]} text-brick-primary drop-shadow-xs`}
            weight="fill"
          />
        </div>
      </div>
      
      {/* Loading message */}
      <p className={`${textSizeClasses[size]} text-neutral-primary font-medium text-center max-w-xs`}>
        {message}
      </p>
      
      {/* Animated dots */}
      <div className="flex space-x-1">
        <div 
          className="w-2 h-2 bg-moss-primary rounded-full animate-bounce" 
          style={{ animationDelay: '0ms' } as React.CSSProperties}
        ></div>
        <div 
          className="w-2 h-2 bg-moss-primary rounded-full animate-bounce" 
          style={{ animationDelay: '100ms' } as React.CSSProperties}
        ></div>
        <div 
          className="w-2 h-2 bg-moss-primary rounded-full animate-bounce" 
          style={{ animationDelay: '200ms' } as React.CSSProperties}
        ></div>
      </div>
    </div>
  );
};

// Full-screen loading overlay variant
export const LoadingOverlay: React.FC<LoadingSpinnerProps> = ({ 
  message = "Even geduld...", 
  size = 'lg' 
}) => {
  return (
    <div className="fixed inset-0 bg-white/90 backdrop-blur-xs flex items-center justify-center z-9999">
      <div className="bg-white rounded-lg shadow-xl p-8 border border-neutral-primary/20 max-w-md mx-4">
        <LoadingSpinner message={message} size={size} />
      </div>
    </div>
  );
};

export default LoadingSpinner; 